import { useMemo, useState } from "react";
import { AnalyticsScreenProvider, Button, Flex, Link, OnboardingLayout, Text } from "@wayflyer/flyui";
import { IconArrowLeft24Line, IconArrowRight24Line } from "@wayflyer/flyui-icons/24/line";
import { useBankConnectionLogoResolvers, useBankConnectionLogos } from "bankconnect";
import dayjs from "dayjs";
import { usePostHog } from "posthog-js/react";
import { FormattedMessage, useIntl } from "react-intl";
import { formatDate } from "shared-ui";
import { useCustomerAppConfig } from "undercarriage";

import { IncompleteStepsModal } from "../../../components/incomplete-steps-modal";
import { PAGE_TYPE, useFundingApplicationNavigation } from "../../../hooks/navigation-next";
import { useApplicationSubmit } from "../../../hooks/use-application-submit";
import { BankControllerProvider, useBankControllerContext } from "../hooks/bank-controller-context";
import { useBankAccountsUploadManager } from "../hooks/use-bank-accounts-upload-manager";
import { useBankStatementExtractionStatuses } from "../hooks/use-bank-statement-extraction-statuses";

import { AccountDetailsPanel } from "./account-details-panel/account-details-panel";
import { BankAccountsList } from "./accounts-list/accounts-list";
import { ConnectModal } from "./connect-modal/connect-modal";
import { ExtractedStatements } from "./extracted-statements/extracted-statements";
import { MESSAGES } from "./message-definitions";
import { RequirementsModal } from "./requirements-modal/requirements-modal";
import { UploadStatementsPanel } from "./upload-statements-panel/upload-statements-panel";
import type { SelectedAccount } from "./utils";

const BankingAccountsPageContent = () => {
  const intl = useIntl();
  const posthog = usePostHog();
  const {
    bankingConnections,
    bankConnectProps,
    basiqClient,
    countryForm,
    countryOptions,
    defaultCountry,
    isConnectionUnsupportedCountry,
    isConnecting,
    handleConnect,
    handleBack,
    handleNext,
    ready,
    isSubmitting,
    requiredStatementMonths,
  } = useBankControllerContext();

  const {
    data: { customerDomain },
  } = useCustomerAppConfig();

  const navigationContext = useFundingApplicationNavigation({ currentPage: PAGE_TYPE.BANKING });
  const applicationSubmit = useApplicationSubmit({ currentPage: PAGE_TYPE.BANKING });

  const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
  const [isRequirementsModalOpen, setIsRequirementsModalOpen] = useState(false);
  const [isUploadStatementsPanelOpen, setIsUploadStatementsPanelOpen] = useState(false);
  const [isAccountDetailsPanelOpen, setIsAccountDetailsPanelOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<SelectedAccount | null>(null);

  const statementPeriodStart = useMemo(
    () => formatDate(dayjs().subtract(requiredStatementMonths, "month").toISOString()),
    [requiredStatementMonths]
  );

  const { uploadsState, onFilesAdded, onFileRemoved, onFileRetry } = useBankAccountsUploadManager(customerDomain);
  const uploadsInProgress = uploadsState.uploadsStarted && !uploadsState.uploadsComplete;
  const confirmedUploads = uploadsState.uploads.flatMap((upload) => (upload.uploadedFile ? [upload.uploadedFile] : []));

  const extractionStatusItems = useBankStatementExtractionStatuses(customerDomain, confirmedUploads);
  const extractedAccounts = extractionStatusItems.flatMap(({ status }) =>
    status?.extractionState === "completed"
      ? status.accounts.map((account) => ({
          objectKey: status.objectKey,
          institutionName: status.institutionName,
          businessName: status.businessName,
          account,
        }))
      : []
  );
  const pendingOrFailedExtractions = extractionStatusItems.filter(
    ({ status }) => status?.extractionState !== "completed"
  );

  const logoResolvers = useBankConnectionLogoResolvers({
    getInstitutionLogoPath: bankConnectProps.plaidConnectProps.getInstitutionLogoPath,
    basiqClient,
  });
  const { logoLoadingStates } = useBankConnectionLogos({
    bankConnections: bankingConnections,
    resolvers: logoResolvers,
  });

  const onContinueConnect = () => {
    handleConnect();
    setIsConnectModalOpen(false);
  };

  const onNext = () => {
    if (!navigationContext.nextPath) {
      applicationSubmit.submitApplication();
      return;
    }
    handleNext();
  };

  return (
    <>
      <IncompleteStepsModal {...applicationSubmit} />
      <OnboardingLayout
        variant="wide"
        title={intl.formatMessage(MESSAGES.pageTitle)}
        subtitle={intl.formatMessage(MESSAGES.pageSubtitle)}
        actions={{
          primary: {
            content: navigationContext.nextButtonLabel,
            onAction: onNext,
            disabled: uploadsInProgress || !ready,
            loading: isSubmitting || applicationSubmit.isSubmitting,
            icon: IconArrowRight24Line,
            analyticsId: "banking-accounts-page-next-button",
          },
          secondary: {
            content: navigationContext.backButtonLabel,
            icon: IconArrowLeft24Line,
            onAction: handleBack,
            analyticsId: "banking-accounts-page-back-button",
          },
        }}
      >
        <Flex direction="column" gap="10">
          <BankAccountsList
            bankingConnections={bankingConnections}
            extractedAccounts={extractedAccounts}
            logoLoadingStates={logoLoadingStates}
            onSelectAccount={(account) => {
              setSelectedAccount(account);
              setIsAccountDetailsPanelOpen(true);
            }}
          />

          <ExtractedStatements items={pendingOrFailedExtractions} />

          <Flex gap="2">
            <Button variant="Primary" onClick={() => setIsConnectModalOpen(true)}>
              {intl.formatMessage(MESSAGES.addAccountInstantly)}
            </Button>
            <Button
              variant="Secondary"
              onClick={() => {
                posthog?.capture("onboarding_bank_upload_selected", { upload_type: "bank_statement" });
                setIsUploadStatementsPanelOpen(true);
              }}
            >
              {intl.formatMessage(MESSAGES.addStatementsManually)}
            </Button>
          </Flex>

          <Flex direction="column" gap="1">
            <Text fontWeight="semiBold" size="xs">
              {intl.formatMessage(MESSAGES.requiredFromYouHeading)}
            </Text>
            <Text size="xs">
              <FormattedMessage
                {...MESSAGES.requiredFromYouBodyText}
                values={{
                  startDate: statementPeriodStart,
                  viewMore: (
                    <Link onClick={() => setIsRequirementsModalOpen(true)}>
                      {intl.formatMessage(MESSAGES.viewMore)}
                    </Link>
                  ),
                }}
              />
            </Text>
          </Flex>
        </Flex>
      </OnboardingLayout>

      <UploadStatementsPanel
        isOpen={isUploadStatementsPanelOpen}
        onClose={() => setIsUploadStatementsPanelOpen(false)}
        uploadsState={uploadsState}
        onFilesAdded={onFilesAdded}
        onFileRemoved={onFileRemoved}
        onFileRetry={onFileRetry}
      />

      <AccountDetailsPanel
        isOpen={isAccountDetailsPanelOpen}
        selectedAccount={selectedAccount}
        onClose={() => setIsAccountDetailsPanelOpen(false)}
        logoLoadingStates={logoLoadingStates}
      />

      <ConnectModal
        isOpen={isConnectModalOpen}
        onClose={() => setIsConnectModalOpen(false)}
        onContinue={onContinueConnect}
        countryForm={countryForm}
        countryOptions={countryOptions}
        defaultCountry={defaultCountry}
        isConnectionUnsupportedCountry={isConnectionUnsupportedCountry}
        isConnecting={isConnecting}
      />

      <RequirementsModal
        isOpen={isRequirementsModalOpen}
        onClose={() => setIsRequirementsModalOpen(false)}
        requiredStatementMonths={requiredStatementMonths}
      />
    </>
  );
};

export const BankingAccountsPage = () => (
  <AnalyticsScreenProvider screen="onboarding-banking-accounts">
    <BankControllerProvider>
      <BankingAccountsPageContent />
    </BankControllerProvider>
  </AnalyticsScreenProvider>
);
